import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { bindActionCreators } from 'redux';
import {
  Card,
  CardBody,
  CardTitle,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import { Loading } from '../../../../loadbar/Loading';
import {
  clearEarthquakeInfo as clearEarthquakeInfoAc,
  getEarthquakeInfo as getEarthquakeInfoAc,
  getEarthquakeReferences as getEarthquakeReferencesAc,
  sortReferences as sortReferencesAc,
  toggleColumns as toggleColumnsAc,
} from '../../../../../redux/earthquakes/info/earthquakeInfoActions';
import { parseAndShortenFloat } from '../../../../../helperFunctions/helperFunctions';
import { SingleResultGrid } from '../../../../grid/SingleResultGrid';
import { Column } from '../../../../grid/Column';
import { ReferencesTable } from '../../../../table/ReferencesTable';
import { CommentsSection } from '../../../../form/CommentsSection';
import { EditIcon } from '../../../../icon/EditIcon';
import { isDataManager } from '../../../../../role/roleUtils';
import { TitleTooltip } from '../../../../form/TitleToolTip';

const renderEditLink = id => (
  <Link aria-label={`Edit Earthquake ${id}`} to={`/view/hazards/earthquake/update/${id}`} target="_blank" rel="noopener noreferrer">
    <EditIcon />
  </Link>
);

class EarthquakeInfoPageUnwrapped extends Component {
  constructor(props) {
    super(props);
    this.handleToggleColumns = this.handleToggleColumns.bind(this);
  }

  componentDidMount() {
    document.title = 'NCEI Hazard Earthquake Information';
    const {
      match: { params }, getEarthquakeReferences, getEarthquakeInfo,
    } = this.props;
    getEarthquakeReferences({ id: params.eqId });
    getEarthquakeInfo({ id: params.eqId });
  }

  componentWillUnmount() {
    const { clearEarthquakeInfo } = this.props;
    clearEarthquakeInfo();
  }

  handleToggleColumns({ visibleColumns, sort }) {
    const {
      visibleColumns: preVisibleColumns, sort: prevSort, sortReferences, toggleColumns,
    } = this.props;
    let changed = false;
    if (!visibleColumns.sort().equals(preVisibleColumns.sort())) {
      toggleColumns(visibleColumns);
      changed = true;
    }
    if (!sort.equals(prevSort)) {
      sortReferences(sort);
      changed = true;
    }
    return changed;
  }

  render() {
    const {
      fetched, earthquake, references, sort, visibleColumns, match: { params: { eqId } }, authorities,
    } = this.props;

    const dataManager = isDataManager(authorities);
    if (!fetched) {
      return (<Loading />);
    }


    return (
      <div className="haz-results-container haz-page-container">
        <Card className="haz-page-card">
          <CardBody>
            <CardTitle tag="h1" className="haz-page-card-title">Significant Earthquake Information</CardTitle>
            {dataManager && renderEditLink(eqId)}
            <div className="haz-info-content">

              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">Earthquake Event Information</CardTitle>
                    <SingleResultGrid data={earthquake}>
                      <Column id="year" helpModalId="year" width={75}>Year</Column>
                      <Column id="month" helpModalId="month" width={50}>Mo</Column>
                      <Column id="day" helpModalId="day" width={50}>Dy</Column>
                      <Column id="hour" helpModalId="hour" width={50}>Hr</Column>
                      <Column id="minute" helpModalId="minute" width={50}>Mn</Column>
                      <Column id="second" helpModalId="second" width={75}>Sec</Column>
                      <Column id="locationName" helpModalId="eqLocName" width={200}>Location</Column>
                      <Column id="latitude" helpModalId="eqLatitude" width={100} cell={({ value }) => (<div>{parseAndShortenFloat(value)}</div>)}>Latitude</Column>
                      <Column id="longitude" helpModalId="eqLongitude" width={100} cell={({ value }) => (<div>{parseAndShortenFloat(value)}</div>)}>Longitude</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">Earthquake Parameters</CardTitle>
                    <SingleResultGrid data={earthquake}>
                      <Column id="eqDepth" helpModalId="eqFocalDepth" width={100}>Focal Depth (km)</Column>
                      <Column id="eqMagMw" helpModalId="eqMwMag" width={100}>Mw</Column>
                      <Column id="eqMagMs" helpModalId="eqMsMag" width={100}>Ms</Column>
                      <Column id="eqMagMb" helpModalId="eqMbMag" width={100}>Mb</Column>
                      <Column id="eqMagMl" helpModalId="eqMlMag" width={100}>Ml</Column>
                      <Column id="eqMagMfa" helpModalId="eqMfaMag" width={100}>Mfa</Column>
                      <Column id="eqMagUnk" helpModalId="eqUnkMag" width={100}>Unk</Column>
                      <Column id="intensity" helpModalId="eqIntensity" width={100}>MMI Int</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">Earthquake Effects</CardTitle>
                    <SingleResultGrid data={earthquake}>
                      <Column id="deaths" width={100} helpModalId="eqDeaths">Deaths</Column>
                      <Column id="deathsAmountOrder" width={125} helpModalId="eqDeathsDescription">Death Description</Column>
                      <Column id="missing" width={100} helpModalId="eqMissing">Missing</Column>
                      <Column id="missingAmountOrder" width={125} helpModalId="eqMissingDescription">Missing Description</Column>
                      <Column id="injuries" width={100} helpModalId="eqInjuries">Injuries</Column>
                      <Column id="injuriesAmountOrder" width={125} helpModalId="eqInjuriesDescription">Injuries Description</Column>
                      <Column id="damageMillionsDollars" width={100} helpModalId="eqDamage">Damage ($Mil)</Column>
                      <Column id="damageAmountOrder" width={125} helpModalId="eqDamageDescription">Damage Description</Column>
                      <Column id="housesDestroyed" width={100} helpModalId="eqHousesDestroyed">Houses Destroyed</Column>
                      <Column id="housesDestroyedAmountOrder" width={125} helpModalId="eqHousesDestroyedDescription">Houses Destroyed Description</Column>
                      <Column id="housesDamaged" width={100} helpModalId="eqHousesDamaged">Houses Damaged</Column>
                      <Column id="housesDamagedAmountOrder" width={125} helpModalId="eqHousesDamagedDescription">Houses Damaged Description</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>


              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">Total Effects (Earthquake and Tsunami, Volcano, etc.)</CardTitle>
                    <SingleResultGrid data={earthquake}>
                      <Column id="deathsTotal" width={100} helpModalId="eqTotalDeaths">Total Deaths</Column>
                      <Column id="deathsAmountOrderTotal" width={125} helpModalId="eqTotalDeathsDescription">Total Death Description</Column>
                      <Column id="missingTotal" width={100} helpModalId="eqTotalMissing">Total Missing</Column>
                      <Column id="missingAmountOrderTotal" width={125} helpModalId="eqTotalMissingDescription">Total Missing Description</Column>
                      <Column id="injuriesTotal" width={100} helpModalId="eqTotalInjuries">Total Injuries</Column>
                      <Column id="injuriesAmountOrderTotal" width={125} helpModalId="eqTotalInjuriesDescription">Total Injuries Description</Column>
                      <Column id="damageMillionsDollarsTotal" width={100} helpModalId="eqTotalDamage">Total Damage ($Mil)</Column>
                      <Column id="damageAmountOrderTotal" width={125} helpModalId="eqTotalDamageDescription">Total Damage Description</Column>
                      <Column id="housesDestroyedTotal" width={100} helpModalId="eqTotalHousesDestroyed">Total Houses Destroyed</Column>
                      <Column id="housesDestroyedAmountOrderTotal" width={125} helpModalId="eqTotalHousesDestroyedDescription">Total Houses Destroyed Description</Column>
                      <Column id="housesDamagedTotal" width={100} helpModalId="eqTotalHousesDamaged">Total Houses Damaged</Column>
                      <Column id="housesDamagedAmountOrderTotal" width={125} helpModalId="eqTotalHousesDamagedDescription">Total Houses Damaged Description</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">
                      <TitleTooltip label="Comments" helpModalId="commentDisclaimer" />
                    </CardTitle>
                    <CommentsSection title="">{earthquake.get('comments')}</CommentsSection>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section haz-reference-info">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">References for the Significant Earthquake</CardTitle>
                    <ReferencesTable
                      references={references}
                      handleToggleColumns={this.handleToggleColumns}
                      sort={sort}
                      visibleColumns={visibleColumns}
                    />
                  </CardBody>
                </Card>
              </div>

            </div>

          </CardBody>
        </Card>

      </div>
    );
  }
}


EarthquakeInfoPageUnwrapped.propTypes = {
  match: PropTypes.shape({ params: PropTypes.shape({ eqId: PropTypes.string }) }).isRequired,
  fetched: PropTypes.bool.isRequired,
  clearEarthquakeInfo: PropTypes.func.isRequired,
  getEarthquakeInfo: PropTypes.func.isRequired,
  getEarthquakeReferences: PropTypes.func.isRequired,
  earthquake: ImmutablePropTypes.map.isRequired,
  references: ImmutablePropTypes.list.isRequired,
  sortReferences: PropTypes.func.isRequired,
  sort: ImmutablePropTypes.list.isRequired,
  visibleColumns: ImmutablePropTypes.list.isRequired,
  toggleColumns: PropTypes.func.isRequired,
  authorities: ImmutablePropTypes.list.isRequired,
};

const mapStateToProps = (state) => {
  const fetchedEarthquake = state.getIn(['earthquakeInfoPage', 'fetchedEarthquake']);
  const fetchedReferences = state.getIn(['earthquakeInfoPage', 'fetchedReferences']);
  return {
    fetched: [fetchedEarthquake, fetchedReferences].every(v => v),
    earthquake: state.getIn(['earthquakeInfoPage', 'earthquake']),
    references: state.getIn(['earthquakeInfoPage', 'references']),
    sort: state.getIn(['earthquakeInfoPage', 'sort']),
    visibleColumns: state.getIn(['earthquakeInfoPage', 'visibleColumns']),
    sortReferences: PropTypes.func.isRequired,
    toggleColumns: PropTypes.func.isRequired,
  };
};

const mapDispatchToProps = dispatch => bindActionCreators({
  clearEarthquakeInfo: clearEarthquakeInfoAc,
  getEarthquakeInfo: getEarthquakeInfoAc,
  getEarthquakeReferences: getEarthquakeReferencesAc,
  sortReferences: sortReferencesAc,
  toggleColumns: toggleColumnsAc,
}, dispatch);

export const EarthquakeInfoPage = connect(mapStateToProps, mapDispatchToProps)(EarthquakeInfoPageUnwrapped);
